import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { supabase, Nomination, JuryVote, Category } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import NominationDetails from '../nominations/NominationDetails';

interface JuryVotingFormProps {
  nomination: Nomination;
  category?: Category;
  existingVote?: JuryVote;
  onClose: () => void;
  onSubmit: () => void;
}

/**
 * Componente de input de puntuación.
 *
 * IMPORTANTE: declarado FUERA de `JuryVotingForm`. Si se declara dentro,
 * cada render del padre crea una nueva referencia de componente y React
 * desmonta/remonta cada input — el foco se pierde y el navegador hace
 * scroll al re-enfocar el input recién montado. Mantenerlo fuera (o
 * memoizarlo con identidad estable) es lo que evita ese comportamiento.
 */
const ScoreInput: React.FC<{
  label: string;
  description: string;
  value: number;
  onChange: (val: number) => void;
}> = ({ label, description, value, onChange }) => {
  const [text, setText] = useState<string>(value ? String(value) : '');
  const [outOfRange, setOutOfRange] = useState(false);

  useEffect(() => {
    setText(value ? String(value) : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleChange = (raw: string) => {
    if (raw === '') {
      setText('');
      setOutOfRange(false);
      onChange(0);
      return;
    }
    const cleaned = raw.replace(',', '.');
    if (!/^\d*\.?\d*$/.test(cleaned)) return;
    setText(cleaned);
    const n = parseFloat(cleaned);
    if (!isNaN(n)) {
      if (n > 5 || n < 0) {
        // Sin auto-clamp: marcamos error y mantenemos el valor en 0 hasta
        // que el usuario corrija (la validación de submit lo bloqueará).
        setOutOfRange(true);
        onChange(0);
      } else {
        setOutOfRange(false);
        onChange(n);
      }
    }
  };

  const handleBlur = () => {
    if (text === '' || text === '.') {
      setText('');
      setOutOfRange(false);
      onChange(0);
      return;
    }
    const n = parseFloat(text);
    if (isNaN(n)) {
      setText('');
      setOutOfRange(false);
      onChange(0);
    } else if (n > 5 || n < 0) {
      // No "corregir" automáticamente: dejamos el texto que escribió el
      // usuario y el aviso visible para que lo cambie él mismo.
      setOutOfRange(true);
      onChange(0);
    } else {
      setOutOfRange(false);
      onChange(n);
    }
  };

  return (
    <div className="criteria-group">
      <div className="criteria-header">{label}</div>
      <div className="criteria-description">{description}</div>
      <div className="score-input">
        <input
          type="text"
          inputMode="decimal"
          value={text}
          onChange={(e) => handleChange(e.target.value)}
          onBlur={handleBlur}
          placeholder="0"
          required
          aria-invalid={outOfRange}
          style={{
            MozAppearance: 'textfield',
            borderColor: outOfRange ? 'var(--color-error)' : undefined,
          }}
        />
        <span className="score-label">/ 5.00</span>
      </div>
      {outOfRange && (
        <div
          className="form-helper"
          style={{ color: 'var(--color-error)', marginTop: 6 }}
        >
          La puntuación debe estar entre 0 y 5.
        </div>
      )}
    </div>
  );
};

const JuryVotingForm: React.FC<JuryVotingFormProps> = ({
  nomination,
  category,
  existingVote,
  onClose,
  onSubmit,
}) => {
  const { user } = useAuth();
  const [showDetails, setShowDetails] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Criterios para personas
  const [trayectoriaProfesional, setTrayectoriaProfesional] = useState<number>(0);
  const [actividadesDestacadas, setActividadesDestacadas] = useState<number>(0);
  const [compromisoDifusion, setCompromisoDifusion] = useState<number>(0);

  // Criterios para proyectos
  const [creatividadInnovacion, setCreatividadInnovacion] = useState<number>(0);
  const [impactoCiudadania, setImpactoCiudadania] = useState<number>(0);
  const [transferenciaDifusion, setTransferenciaDifusion] = useState<number>(0);
  const [sostenibilidad, setSostenibilidad] = useState<number>(0);
  const [cambioCultura, setCambioCultura] = useState<number>(0);
  const [equilibrioEficacia, setEquilibrioEficacia] = useState<number>(0);
  const [evaluacionIniciativa, setEvaluacionIniciativa] = useState<number>(0);

  // Criterios Benchmarking
  const [benchmarkImpactoSostenido, setBenchmarkImpactoSostenido] = useState<number>(0);
  const [benchmarkReplicabilidad, setBenchmarkReplicabilidad] = useState<number>(0);
  const [benchmarkAdopcion, setBenchmarkAdopcion] = useState<number>(0);
  const [benchmarkCapacidadInspirar, setBenchmarkCapacidadInspirar] = useState<number>(0);

  const [generalComment, setGeneralComment] = useState('');

  const isPersonal = category?.type === 'personal';
  const isBenchmark = category?.type === 'benchmark';

  useEffect(() => {
    if (existingVote) {
      // Cargar votos existentes
      if (isBenchmark) {
        setBenchmarkImpactoSostenido(existingVote.benchmark_impacto_sostenido || 0);
        setBenchmarkReplicabilidad(existingVote.benchmark_replicabilidad || 0);
        setBenchmarkAdopcion(existingVote.benchmark_adopcion_referencia || 0);
        setBenchmarkCapacidadInspirar(existingVote.benchmark_capacidad_inspirar || 0);
      } else if (isPersonal) {
        setTrayectoriaProfesional(existingVote.trayectoria_profesional || 0);
        setActividadesDestacadas(existingVote.actividades_destacadas || 0);
        setCompromisoDifusion(existingVote.compromiso_difusion || 0);
      } else {
        setCreatividadInnovacion(existingVote.creatividad_innovacion || 0);
        setImpactoCiudadania(existingVote.impacto_ciudadania || 0);
        setTransferenciaDifusion(existingVote.transferencia_difusion || 0);
        setSostenibilidad(existingVote.sostenibilidad || 0);
        setCambioCultura(existingVote.cambio_cultura || 0);
        setEquilibrioEficacia(existingVote.equilibrio_eficacia || 0);
        setEvaluacionIniciativa(existingVote.evaluacion_iniciativa || 0);
      }
      setGeneralComment(existingVote.general_comment || '');
    }
  }, [existingVote, isPersonal, isBenchmark]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!generalComment.trim()) {
      setError('El comentario general es obligatorio.');
      return;
    }

    setLoading(true);

    try {
      const voteData: any = {
        nomination_id: nomination.id,
        jury_member_id: user?.id,
        general_comment: generalComment.trim(),
      };

      if (isBenchmark) {
        voteData.benchmark_impacto_sostenido = benchmarkImpactoSostenido;
        voteData.benchmark_replicabilidad = benchmarkReplicabilidad;
        voteData.benchmark_adopcion_referencia = benchmarkAdopcion;
        voteData.benchmark_capacidad_inspirar = benchmarkCapacidadInspirar;
      } else if (isPersonal) {
        voteData.trayectoria_profesional = trayectoriaProfesional;
        voteData.actividades_destacadas = actividadesDestacadas;
        voteData.compromiso_difusion = compromisoDifusion;
      } else {
        voteData.creatividad_innovacion = creatividadInnovacion;
        voteData.impacto_ciudadania = impactoCiudadania;
        voteData.transferencia_difusion = transferenciaDifusion;
        voteData.sostenibilidad = sostenibilidad;
        voteData.cambio_cultura = cambioCultura;
        voteData.equilibrio_eficacia = equilibrioEficacia;
        voteData.evaluacion_iniciativa = evaluacionIniciativa;
      }

      if (existingVote) {
        // Actualizar voto existente
        const { error: updateError } = await supabase
          .schema('premios')
          .from('jury_votes')
          .update(voteData)
          .eq('id', existingVote.id);

        if (updateError) throw updateError;
      } else {
        // Crear nuevo voto
        const { error: insertError } = await supabase
          .schema('premios')
          .from('jury_votes')
          .insert([voteData]);

        if (insertError) throw insertError;
      }

      onSubmit();
    } catch (err: any) {
      setError(err.message || 'Error al guardar la votación');
      console.error('Error submitting vote:', err);
    } finally {
      setLoading(false);
    }
  };

  const renderScoreInput = (
    label: string,
    description: string,
    value: number,
    onChange: (val: number) => void
  ) => (
    <ScoreInput label={label} description={description} value={value} onChange={onChange} />
  );

  return (
    <div style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={onClose}
        aria-label="Cerrar"
        style={{
          position: 'absolute',
          top: -8,
          right: -8,
          width: 36,
          height: 36,
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.06)',
          border: '1px solid var(--color-border)',
          color: 'var(--color-text)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.2s ease',
          zIndex: 10,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(239, 68, 68, 0.15)';
          e.currentTarget.style.borderColor = 'var(--color-error)';
          e.currentTarget.style.color = 'var(--color-error)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.06)';
          e.currentTarget.style.borderColor = 'var(--color-border)';
          e.currentTarget.style.color = 'var(--color-text)';
        }}
      >
        <X size={18} />
      </button>

      <div className="modal-header" style={{ paddingRight: 40 }}>
        <h2>Evaluación de Candidatura</h2>
      </div>

      {showDetails ? (
        <>
          <NominationDetails
            nomination={nomination}
            category={category}
            onClose={onClose}
            showActions={false}
            showClose={false}
            noDetailsScroll={true}
            showTitle={false}
          />
          <div className="modal-footer">
            <button
              className="btn btn-primary"
              onClick={() => setShowDetails(false)}
            >
              Continuar con la evaluación
            </button>
          </div>
        </>
      ) : (
        <form onSubmit={handleSubmit}>
          {error && <div className="alert alert-error">{error}</div>}

          <div className="voting-form">
            {isBenchmark ? (
              <>
                {renderScoreInput(
                  'Impacto sostenido',
                  'Impacto sostenido del proyecto, acreditado mediante resultados mantenidos o mejorados en el tiempo.',
                  benchmarkImpactoSostenido,
                  setBenchmarkImpactoSostenido
                )}

                {renderScoreInput(
                  'Replicabilidad y transferibilidad',
                  'Posibilidad de adaptación del proyecto a otras administraciones y existencia de metodología o documentación reutilizable.',
                  benchmarkReplicabilidad,
                  setBenchmarkReplicabilidad
                )}

                {renderScoreInput(
                  'Adopción o referencia por otras administraciones',
                  'Evidenciada mediante experiencias de réplica, adaptación o uso como buena práctica por parte de otras administraciones.',
                  benchmarkAdopcion,
                  setBenchmarkAdopcion
                )}

                {renderScoreInput(
                  'Capacidad de inspirar mejoras en la gestión pública',
                  'Contribución al aprendizaje, la innovación y la mejora organizativa en el sector público.',
                  benchmarkCapacidadInspirar,
                  setBenchmarkCapacidadInspirar
                )}
              </>
            ) : isPersonal ? (
              <>
                {renderScoreInput(
                  'Trayectoria Profesional',
                  'Valora la trayectoria profesional de la persona candidata.',
                  trayectoriaProfesional,
                  setTrayectoriaProfesional
                )}

                {renderScoreInput(
                  'Actividades Destacadas en el Último Año',
                  'Evalúa las actividades más relevantes realizadas recientemente.',
                  actividadesDestacadas,
                  setActividadesDestacadas
                )}

                {renderScoreInput(
                  'Compromiso con la Difusión',
                  'Valora el compromiso de la persona con la difusión de la innovación y el conocimiento.',
                  compromisoDifusion,
                  setCompromisoDifusion
                )}
              </>
            ) : (
              <>
                {renderScoreInput(
                  'Creatividad, Conocimiento e Innovación',
                  'Aplicación de la creatividad y el conocimiento de las personas de la organización a la definición e implantación de servicios públicos orientados a la ciudadanía-usuaria y de carácter novedoso. Nivel de complejidad de gestión del conocimiento. El nivel de la innovación de la iniciativa.',
                  creatividadInnovacion,
                  setCreatividadInnovacion
                )}

                {renderScoreInput(
                  'Impacto sobre la Ciudadanía',
                  'Efectos de la práctica innovadora en las personas usuarias de los servicios o en otros grupos de interés. Magnitud de la población beneficiada. Beneficios obtenidos (reducción de tiempos, trámites, costes, facilidad de uso, etc.). Repercusión en la mejora de la satisfacción.',
                  impactoCiudadania,
                  setImpactoCiudadania
                )}

                {renderScoreInput(
                  'Transferencia y Difusión',
                  'Capacidad de la práctica innovadora de ser aplicada o adaptada o compartida con otras organizaciones. Acciones de benchmarking. Organizaciones que han reproducido la práctica.',
                  transferenciaDifusion,
                  setTransferenciaDifusion
                )}

                {renderScoreInput(
                  'Sostenibilidad de la Iniciativa',
                  'Nivel de consolidación o institucionalización de la práctica innovadora en términos temporales, presupuestarios y de mantenimiento frente a cambios del entorno político, institucional o económico.',
                  sostenibilidad,
                  setSostenibilidad
                )}

                {renderScoreInput(
                  'Cambio en la Cultura Organizativa',
                  'Nivel de implicación de las personas de la organización en la iniciativa. Involucración de las personas para aprovechar su creatividad. Nivel de colaboración entre profesionales públicos.',
                  cambioCultura,
                  setCambioCultura
                )}

                {renderScoreInput(
                  'Equilibrio de Eficacia y Eficiencia',
                  'Grado de consecución de los objetivos perseguidos con la práctica innovadora y aprendizaje destinado a la optimización de procesos y recursos financieros, tecnológicos, materiales, etc. Coste beneficio/efectividad.',
                  equilibrioEficacia,
                  setEquilibrioEficacia
                )}

                {renderScoreInput(
                  'Evaluación de la Iniciativa',
                  'Establecimiento de mecanismos de evaluación de la iniciativa. Aplicación de procedimientos de escucha activa. Medición constante de la calidad.',
                  evaluacionIniciativa,
                  setEvaluacionIniciativa
                )}
              </>
            )}

            <div className="form-group">
              <label className="form-label">Comentario General *</label>
              <textarea
                className="form-textarea"
                value={generalComment}
                onChange={(e) => setGeneralComment(e.target.value)}
                placeholder="Comparte tu valoración cualitativa de la candidatura..."
                rows={4}
                required
              />
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setShowDetails(true)}
            >
              Ver detalles
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? 'Guardando...' : existingVote ? 'Actualizar evaluación' : 'Guardar evaluación'}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
            >
              Cancelar
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default JuryVotingForm;
